import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Video } from 'lucide-react';
import SynchronizedPosePlayer from '../../SynchronizedPosePlayer';
import CanvasPoseOverlayPlayer from '../../CanvasPoseOverlayPlayer';
import FrameAwareCoaching from '../../FrameAwareCoaching';
import DrawingOverlay from './DrawingOverlay';
import { API_URL } from '../../config/api';

const VideoPlayerSection = ({
  isGeneratingOverlay,
  useCanvasOverlay,
  overlayVideoUrl,
  uploadStatus,
  viewingHistoryVideo,
  videoId,
  videoPreview,
  totalFrameCount,
  videoFps,
  analysisResult,
  selectedElement,
  currentFrame,
  frameCoaching,
  handleFrameChange,
  handleBookmark,
  handleActionStepClick,
  onCorrectPhase,
  isRecalculating,
  isCorrected,
}) => {
  const [drawingActive, setDrawingActive] = useState(false);
  const [containerEl, setContainerEl] = useState(null);
  const [poseDataForFrame, setPoseDataForFrame] = useState(null);
  const playerRef = useRef(null);
  const overlayShellRef = useRef(null);

  const toggleDrawMode = useCallback(() => {
    setDrawingActive(prev => !prev);
  }, []);

  const exitDrawMode = useCallback(() => {
    setDrawingActive(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer = null;

    setContainerEl(null);

    const check = () => {
      if (cancelled) return;
      const el = playerRef.current?.getContainerElement?.();
      if (el) {
        setContainerEl(el);
      } else {
        retryTimer = setTimeout(check, 200);
      }
    };

    check();

    const observerTarget = overlayShellRef.current;
    const observer = observerTarget
      ? new MutationObserver(() => check())
      : null;
    observer?.observe(observerTarget, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      observer?.disconnect();
    };
  }, [useCanvasOverlay, overlayVideoUrl, videoId, drawingActive]);

  const handleFrameChangeWithPose = useCallback((frame, framePose) => {
    handleFrameChange(frame, framePose);
    setPoseDataForFrame(framePose || null);
  }, [handleFrameChange]);

  useEffect(() => {
    if (drawingActive && playerRef.current?.getPoseDataForFrame && currentFrame >= 0) {
      const data = playerRef.current.getPoseDataForFrame(currentFrame);
      if (data) setPoseDataForFrame(data);
    }
  }, [drawingActive, currentFrame]);

  const phaseColor = frameCoaching?.element_phase || null;

  // Generating overlay state
  if (isGeneratingOverlay) {
    return (
      <div className="generating-overlay">
        <div className="loading-spinner" />
        <h4>Generating video with pose overlays...</h4>
        <p>This will take 30-60 seconds. Please wait.</p>
      </div>
    );
  }

  // Canvas Overlay Player for large videos (>200 frames)
  if (useCanvasOverlay) {
    return (
      <div className="synchronized-features">
        <div className="feature-header">
          <div className="feature-title">
            <Video className="w-5 h-5" />
            <h5>Video Analysis with Pose Overlay</h5>
          </div>
          <p className="feature-description">
            Interactive frame-by-frame coaching with pose overlay and improvement recommendations.
          </p>
        </div>

        <div className="player-container">
          <div className="player-overlay-shell" ref={overlayShellRef}>
            <CanvasPoseOverlayPlayer
              ref={playerRef}
              key={`canvas-${videoId}`}
              videoId={videoId}
              videoUrl={`${API_URL}/api/v1/video/${videoId}`}
              totalFrames={totalFrameCount}
              fps={videoFps || 30}
              onFrameChange={handleFrameChangeWithPose}
              apiBaseUrl={API_URL}
              drawMode={drawingActive}
              onDrawToggle={toggleDrawMode}
            />
            <DrawingOverlay
              isActive={drawingActive}
              currentFrame={currentFrame}
              videoId={videoId}
              containerEl={containerEl}
              fallbackContainerEl={overlayShellRef.current}
              poseData={poseDataForFrame}
              phaseColor={phaseColor}
              onExit={exitDrawMode}
            />
          </div>
        </div>

        {(frameCoaching || currentFrame > 0) && (
          <div className="coaching-container">
            <FrameAwareCoaching
              frameNumber={currentFrame}
              coaching={frameCoaching}
              onActionStepClick={handleActionStepClick}
              confidence={frameCoaching?.confidence_level}
              elementPhase={frameCoaching?.element_phase}
              showDetailed={true}
              onCorrectPhase={onCorrectPhase}
              isRecalculating={isRecalculating}
              isCorrected={isCorrected}
              elementType={selectedElement}
            />
          </div>
        )}
      </div>
    );
  }

  // Synchronized Pose Player with burned overlay
  if (overlayVideoUrl && overlayVideoUrl.length > 0) {
    return (
      <div className="synchronized-features">
        <div className="feature-header">
          <div className="feature-title">
            <Video className="w-5 h-5" />
            <h5>Video Analysis with Pose Overlay</h5>
          </div>
          <p className="feature-description">
            Interactive frame-by-frame coaching with pose overlay and improvement recommendations.
          </p>
        </div>

        <div className="player-container">
          <div className="player-overlay-shell" ref={overlayShellRef}>
            <SynchronizedPosePlayer
              ref={playerRef}
              key={overlayVideoUrl}
              videoId={videoId}
              analysisData={{
                ...analysisResult,
                videoUrl: overlayVideoUrl.startsWith('blob:') ? overlayVideoUrl : `${API_URL}${overlayVideoUrl}`,
                element_type: selectedElement
              }}
              onFrameChange={handleFrameChangeWithPose}
              onBookmark={handleBookmark}
              drawMode={drawingActive}
              onDrawToggle={toggleDrawMode}
            />
            <DrawingOverlay
              isActive={drawingActive}
              currentFrame={currentFrame}
              videoId={videoId}
              containerEl={containerEl}
              fallbackContainerEl={overlayShellRef.current}
              poseData={null}
              phaseColor={phaseColor}
              onExit={exitDrawMode}
            />
          </div>
        </div>

        {(frameCoaching || currentFrame > 0) && (
          <div className="coaching-container">
            <FrameAwareCoaching
              frameNumber={currentFrame}
              coaching={frameCoaching}
              onActionStepClick={handleActionStepClick}
              confidence={frameCoaching?.confidence_level}
              elementPhase={frameCoaching?.element_phase}
              showDetailed={true}
              onCorrectPhase={onCorrectPhase}
              isRecalculating={isRecalculating}
              isCorrected={isCorrected}
              elementType={selectedElement}
            />
          </div>
        )}
      </div>
    );
  }

  // Video unavailable state (history video with missing file)
  if (uploadStatus === 'complete' && viewingHistoryVideo) {
    return (
      <div className="video-unavailable">
        <Video className="w-8 h-8 text-gray-400 mx-auto mb-2" />
        <h4>Video Unavailable</h4>
        <p>The video file could not be loaded, but your analysis data is shown above.</p>
        <p className="video-unavailable-hint">This can happen when the video was stored on a different server or has been moved.</p>
      </div>
    );
  }

  // Default waiting state
  return (
    <div className="waiting-overlay">
      <p>Preparing video player...</p>
    </div>
  );
};

export default VideoPlayerSection;
